import { apiRequest } from "@/lib/api/client";

// Integração real com o modelo de vínculo do Traccar. O Traccar não expõe
// permissões como um recurso CRUD com "id" próprio — é um link/unlink de
// par de IDs via POST/DELETE /api/permissions {userId, deviceId}. Para ler
// o estado atual (quais dispositivos um usuário já enxerga) usamos
// GET /api/devices?userId= — a forma real e documentada do Traccar para um
// admin consultar os dispositivos visíveis a outro usuário (equivalente ao
// que a própria interface web do Traccar usa; GET /api/permissions não é
// um "listar tudo" e tem um bug conhecido nesta imagem 6.14.5 para vínculos
// de notificação — evitado aqui de propósito).
interface TraccarDeviceRef {
  id: number;
}
interface TraccarGeofenceRef {
  id: number;
}

export const permissionsService = {
  devicesForUser: async (userId: number): Promise<string[]> => {
    const devices = await apiRequest<TraccarDeviceRef[]>(`/api/devices?userId=${userId}`, {
      method: "GET",
      credentials: "include",
    });
    return devices.map((d) => String(d.id));
  },

  linkUserDevice: (userId: number, deviceId: number) =>
    apiRequest<void>("/api/permissions", {
      method: "POST",
      credentials: "include",
      body: { userId, deviceId },
    }),

  unlinkUserDevice: (userId: number, deviceId: number) =>
    apiRequest<void>("/api/permissions", {
      method: "DELETE",
      credentials: "include",
      body: { userId, deviceId },
    }),

  // Vínculo dispositivo↔geocerca. O Traccar não documenta um filtro
  // "?geofenceId=" em GET /api/devices (o filtro reverso não existe para o
  // recurso "principal" Device) — por isso lemos pelo caminho que É
  // documentado: GET /api/geofences?deviceId= (geocercas visíveis a um
  // dispositivo), uma chamada por dispositivo já carregado na tela. Em
  // frotas pequenas isso é barato; se a frota crescer muito vale revisitar.
  geofenceIdsForDevice: async (deviceId: number): Promise<string[]> => {
    const geofences = await apiRequest<TraccarGeofenceRef[]>(
      `/api/geofences?deviceId=${deviceId}`,
      { method: "GET", credentials: "include" },
    );
    return geofences.map((g) => String(g.id));
  },

  // allSettled, não all: um único deviceId inválido (ex.: veículo "extra"
  // só local, nunca cadastrado no Traccar, sem id numérico real) não pode
  // derrubar a leitura de vínculo de TODOS os outros veículos — antes,
  // qualquer id inválido na lista fazia o Promise.all inteiro rejeitar,
  // devolvendo os vínculos como undefined mesmo para dispositivos válidos e
  // corretamente vinculados no Traccar.
  devicesForGeofence: async (geofenceId: number, deviceIds: number[]): Promise<string[]> => {
    const target = String(geofenceId);
    const results = await Promise.allSettled(
      deviceIds.map(async (id) => ({
        id,
        linked: (await permissionsService.geofenceIdsForDevice(id)).includes(target),
      })),
    );
    return results
      .filter((r): r is PromiseFulfilledResult<{ id: number; linked: boolean }> => r.status === "fulfilled")
      .filter((r) => r.value.linked)
      .map((r) => String(r.value.id));
  },

  linkDeviceGeofence: (deviceId: number, geofenceId: number) =>
    apiRequest<void>("/api/permissions", {
      method: "POST",
      credentials: "include",
      body: { deviceId, geofenceId },
    }),

  unlinkDeviceGeofence: (deviceId: number, geofenceId: number) =>
    apiRequest<void>("/api/permissions", {
      method: "DELETE",
      credentials: "include",
      body: { deviceId, geofenceId },
    }),

  // Vínculo usuário↔geocerca — ainda NÃO implementado nesta etapa (adiado
  // para a etapa de permissões avançadas, junto do restante do Backend GPS
  // Drive). Quando for implementado, seguir exatamente este mesmo padrão:
  //   POST/DELETE /api/permissions { userId, geofenceId }
  // simétrico a linkUserDevice/unlinkUserDevice acima.
};
